import type { TopicFollowConfig, TopicFollowStatus, TopicFollowChannel, FollowResult, UnfollowResult } from './types';

/**
 * Client REST minimal du suivi de rubrique (gas/v1/topic-follows).
 * Enveloppe GasBaseRestController : { data: {...}, meta: {...} } ;
 * erreurs WP_Error : { code, message, data }.
 */

interface RestEnvelope<T> {
    data?: T;
    code?: string;
    message?: string;
}

async function call<T>(
    config: TopicFollowConfig,
    method: 'GET' | 'POST' | 'DELETE',
    body?: Record<string, unknown>,
    query?: Record<string, string>,
    path = '',
): Promise<T> {
    let url = config.rest_url + path;
    if (query) {
        const params = new URLSearchParams(query);
        url += (url.includes('?') ? '&' : '?') + params.toString();
    }

    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': config.rest_nonce,
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    const payload = (await response.json().catch(() => ({}))) as RestEnvelope<T>;

    if (!response.ok) {
        throw new Error(payload.message || `HTTP ${response.status}`);
    }

    return (payload.data ?? payload) as T;
}

export function fetchStatus(config: TopicFollowConfig, topicId: number): Promise<TopicFollowStatus> {
    return call<TopicFollowStatus>(config, 'GET', undefined, {
        topic_id: String(topicId),
    }, '/status');
}

export function followTopic(
    config: TopicFollowConfig,
    topicId: number,
    channels: TopicFollowChannel[],
    guest?: { email: string; name?: string },
): Promise<FollowResult> {
    return call<FollowResult>(config, 'POST', {
        topic_id: topicId,
        channels,
        ...(guest ? { email: guest.email, name: guest.name || '' } : {}),
    });
}

export function unfollowTopic(
    config: TopicFollowConfig,
    topicId: number,
    guestEmail?: string,
): Promise<UnfollowResult> {
    return call<UnfollowResult>(config, 'DELETE', {
        topic_id: topicId,
        ...(guestEmail ? { email: guestEmail } : {}),
    });
}
